import { z } from 'zod';

export const JourneyId = z.enum([
  'health-default',
  'medicare-advantage',
  'medicare-supplement',
  'medicare-generic',
  '101',
  '102',
]);
export type JourneyId = z.infer<typeof JourneyId>;

export const GetJourneyRequestDto = z.object({
  journeyId: JourneyId,
  env: z.enum(['production', 'development']),
});

export type GetJourneyRequestDto = z.infer<typeof GetJourneyRequestDto>;

export const MedicareAdvantagePlate = z.enum([
  'medicare_coverage_urgency.advantage',
  'fname-lname-email-location-phone.advantage',
  'gender-dob-dob_category.advantage',
  'additional_coverage.advantage',
  'zip_code.advantage',
  'redirect_typ_medicare_advantage',
]);
export type MedicareAdvantagePlate = z.infer<typeof MedicareAdvantagePlate>;

export const MedicareSupplementPlate = z.enum([
  'medicare_coverage_urgency',
  'fname-lname-email-location-phone',
  'gender-dob-dob_category',
  'gender.supplement',
  'dob-dob_category.supplement',
  'additional_coverage',
  'zip_code',
  'redirect_typ_medicare',
]);
export type MedicareSupplementPlate = z.infer<typeof MedicareSupplementPlate>;

export const MedicareGenericPlate = z.enum([
  'kind_of_medicare_plan',
  'medicare_coverage_urgency.advantage',
  'zip_code',
  'gender-dob-dob_category.skip.with_tooltip',
  'fname-lname-email-location-phone.advantage',
  'redirect_typ_medicare',
  'redirect_quotes_healthcare_com',
  'redirect_med_advantage',
  'redirect_medicare',
]);
export type MedicareGenericPlate = z.infer<typeof MedicareGenericPlate>;

export const HealthPlate = z.enum([
  'redirect_aca_healthcare_com',
  'gender-dob-dob_category.health',
  'coverage_duration',
  'change_pharmacy.health',
  'estimated_spending',
  'zip_code.health',
  'household_size',
  'household_income',
  'household_size-household_income',
  'redirect_typ_health',
  'income_subsidy',
  'quality_life_event',
  'redirect_stm_healthcare_com',
  'insure_who',
  'redirect_medicare_healthcare_com',
  'spouse_children',
  'immediate_coverage',
  'fname-lname-email-location-phone.health',
  'tobacco',
  'senior_check.health',
]);
export type HealthPlate = z.infer<typeof HealthPlate>;

export const SharedPlate = z.enum(['redirect_quotes']);
export type SharedPlate = z.infer<typeof SharedPlate>;

export const AdditionalCoverage = z.enum(['advantage', 'supplement', 'none']);
export type AdditionalCoverage = z.infer<typeof AdditionalCoverage>;

export const AskForZipCode = z.enum(['true', 'false']);
export type AskForZipCode = z.infer<typeof AskForZipCode>;

export const ChangePharmacy = z.enum(['no', 'yes']);
export type ChangePharmacy = z.infer<typeof ChangePharmacy>;

export const Channel = z.enum(['online', 'online_and_in_person']);
export type Channel = z.infer<typeof Channel>;

export const CoverageDuration = z.enum(['permanent', 'temporary', 'not_sure']);
export type CoverageDuration = z.infer<typeof CoverageDuration>;

export const DobCategory = z.enum(['u65', 'o65']);
export type DobCategory = z.infer<typeof DobCategory>;

export const EstimatedSpending = z.enum(['low', 'medium', 'high']);
export type EstimatedSpending = z.infer<typeof EstimatedSpending>;

export const ForWhoHelp = z.enum(['self', 'couple', 'children']);
export type ForWhoHelp = z.infer<typeof ForWhoHelp>;

export const Gender = z.enum(['m', 'f']);
export type Gender = z.infer<typeof Gender>;

export const HasMedicare = z.enum(['no', 'yes']);
export type HasMedicare = z.infer<typeof HasMedicare>;

export const HouseholdIncome = z.enum(['below', 'between', 'over']);
export type HouseholdIncome = z.infer<typeof HouseholdIncome>;

export const HouseholdSize = z.enum(['1', '2', '3', '4', '5', '6']);
export type HouseholdSize = z.infer<typeof HouseholdSize>;

export const ImmediateCoverage = z.enum(['immediately', '2_weeks', 'not_sure']);
export type ImmediateCoverage = z.infer<typeof ImmediateCoverage>;

export const InsuranceType = z.enum([
  'aca',
  'stm_and_alternatives',
  'not_sure',
]);
export type InsuranceType = z.infer<typeof InsuranceType>;

export const InsureWho = z.enum(['just_me', 'myself_and_others']);
export type InsureWho = z.infer<typeof InsureWho>;

export const IsOep = z.enum(['is-oep-yes', 'is-oep-no']);
export type IsOep = z.infer<typeof IsOep>;

export const IsPhoenix = z.enum(['true', 'false']);
export type IsPhoenix = z.infer<typeof IsPhoenix>;

export const KindOfCoverage = z.enum(['a_b', 'a', 'none']);
export type KindOfCoverage = z.infer<typeof KindOfCoverage>;

export const MedicareCoverageUrgency = z.enum([
  'right_away',
  'next_month_or_later',
  'not_sure',
]);
export type MedicareCoverageUrgency = z.infer<typeof MedicareCoverageUrgency>;

export const QualityLifeEvent = z.enum([
  'lost_coverage',
  'got_new_job',
  'had_a_baby',
  'moved_new_state',
  'none_of_these',
]);
export type QualityLifeEvent = z.infer<typeof QualityLifeEvent>;

export const SeniorCheck = z.enum(['u65', 'o65']);
export type SeniorCheck = z.infer<typeof SeniorCheck>;

export const TimeOfOccurrence = z.enum([
  'last_60_days',
  'next_60_days',
  'after_60_days',
]);
export type TimeOfOccurrence = z.infer<typeof TimeOfOccurrence>;

export const Tobacco = z.enum(['n', 'y']);
export type Tobacco = z.infer<typeof Tobacco>;

export const TypeOfHelp = z.enum([
  'therapy',
  'medication',
  'therapy_and_medication',
  'i_dont_know',
]);
export type TypeOfHelp = z.infer<typeof TypeOfHelp>;

export const TypeOfPayment = z.enum(['insurance', 'cash']);
export type TypeOfPayment = z.infer<typeof TypeOfPayment>;

export const KindOfMedicarePlan = z.enum([
  'medicare_advantage',
  'medicare_supplement_medigap',
  'not_sure',
]);
export type KindOfMedicarePlan = z.infer<typeof KindOfMedicarePlan>;

export const Questions = z.object({
  additional_coverage: AdditionalCoverage,
  ask_for_zip_code: AskForZipCode,
  change_pharmacy: ChangePharmacy,
  channel: Channel,
  coverage_duration: CoverageDuration,
  dob_category: DobCategory,
  estimated_spending: EstimatedSpending,
  for_who_help: ForWhoHelp,
  gender: Gender,
  has_medicare: HasMedicare,
  household_income: HouseholdIncome,
  household_size: HouseholdSize,
  immediate_coverage: ImmediateCoverage,
  insurance_type: InsuranceType,
  insure_who: InsureWho,
  is_oep: IsOep,
  is_phoenix: IsPhoenix,
  kind_of_coverage: KindOfCoverage,
  kind_of_medicare_plan: KindOfMedicarePlan,
  medicare_coverage_urgency: MedicareCoverageUrgency,
  quality_life_event: QualityLifeEvent,
  senior_check: SeniorCheck,
  time_of_occurrence: TimeOfOccurrence,
  tobacco: Tobacco,
  type_of_help: TypeOfHelp,
  type_of_payment: TypeOfPayment,
});
export type Questions = z.infer<typeof Questions>;

export const Question = Questions.keyof();
export type Question = z.infer<typeof Question>;

export const PlateName = MedicareAdvantagePlate.or(MedicareSupplementPlate)
  .or(MedicareGenericPlate)
  .or(HealthPlate)
  .or(SharedPlate);
export type PlateName = z.infer<typeof PlateName>;

export const TransitionDto = z.object({
  plate: PlateName,
  questions: Questions.partial(),
  validate: z.boolean(),
});

export type TransitionDto = z.infer<typeof TransitionDto>;

const PlateDto = z
  .object({
    id: PlateName,
    transitions: TransitionDto.array(),
  })
  .partial();

export const GetJourneyResponseDto = z.object({
  id: z.string(),
  plates: z.record(PlateName, PlateDto),
  start: PlateName,
});

export type GetJourneyResponseDto = z.infer<typeof GetJourneyResponseDto>;
